import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Sparkles, Trash2, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, mediaUrl, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { useChannels } from "@/components/ChannelGrid";
import { Empty, MiniPlay, StatusBadge, fieldCls } from "@/components/dashboard/Shared";

export function UploadTrack({ onDone }) {
  const api = useApi();
  const [title, setTitle] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [busy, setBusy] = useState(false);
  const audioRef = useRef(null);
  const coverRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    const audio = audioRef.current.files[0];
    if (!audio) { toast.error("Elige un archivo MP3 o WAV"); return; }
    const fd = new FormData();
    fd.append("title", title); fd.append("lyrics", lyrics); fd.append("audio", audio);
    if (coverRef.current.files[0]) fd.append("cover", coverRef.current.files[0]);
    setBusy(true);
    try {
      await api("post", "/tracks", fd);
      toast.success("Canción subida");
      setTitle(""); setLyrics(""); e.target.reset(); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="upload-track-form" className="rounded-xl border border-white/10 bg-[#12121A] p-6">
      <p className="font-heading text-lg font-bold text-white">Subir canción</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Input data-testid="upload-track-title" required placeholder="Título" value={title} onChange={(e) => setTitle(e.target.value)} className={fieldCls} />
        <label className="flex items-center gap-2 text-sm text-slate-400">Audio (MP3/WAV, máx. 25 MB)
          <input data-testid="upload-track-audio" ref={audioRef} type="file" accept="audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav" className="text-xs file:mr-2 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-white" />
        </label>
        <Textarea data-testid="upload-track-lyrics" placeholder="Letra (opcional)" value={lyrics} onChange={(e) => setLyrics(e.target.value)} className={`${fieldCls} md:col-span-2`} />
        <label className="flex items-center gap-2 text-sm text-slate-400">Portada (opcional)
          <input data-testid="upload-track-cover" ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" className="text-xs file:mr-2 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-white" />
        </label>
      </div>
      <Button data-testid="upload-track-submit" disabled={busy} className="mt-5 rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]"><Upload className="mr-1 h-4 w-4" />{busy ? "Subiendo..." : "Subir"}</Button>
    </form>
  );
}

function TrackRow({ t, channels, reload }) {
  const api = useApi();
  const { me, refresh } = useMe();
  const [slug, setSlug] = useState("");
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const chName = channels?.find((c) => c.slug === t.channel_slug)?.name;
  return (
    <div data-testid={`track-row-${t.id}`} className="flex flex-col gap-4 border-b border-white/5 py-5 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <MiniPlay track={t} testId={`track-play-${t.id}`} />
        {t.cover_url && <img src={mediaUrl(t.cover_url)} alt="" className="h-12 w-12 rounded object-cover" />}
        <div className="min-w-0">
          <p className="truncate font-heading font-bold text-white">{t.title} {t.featured && <span data-testid={`track-featured-${t.id}`} className="ml-1 font-mono text-[10px] text-[#FF7A45]">DESTACADA</span>}</p>
          <p className="font-mono text-[11px] text-slate-500">{t.plays} reproducciones · {t.likes} me gusta {chName && `· ${chName}`}</p>
          {t.radio_status === "rejected" && t.radio_comment && <p className="mt-1 text-xs text-red-300">Curador: “{t.radio_comment}”</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={t.radio_status} testId={`track-status-${t.id}`} />
        {["none", "rejected"].includes(t.radio_status) && (
          <>
            <Select value={slug} onValueChange={setSlug}>
              <SelectTrigger data-testid={`track-channel-select-${t.id}`} className={`h-9 w-40 ${fieldCls}`}><SelectValue placeholder="Elegir canal" /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{channels?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" data-testid={`track-apply-btn-${t.id}`} disabled={!slug} onClick={() => run(() => api("post", `/tracks/${t.id}/apply`, { channel_slug: slug }), "Postulación enviada al curador")} className="rounded-full bg-white text-black hover:bg-slate-200">Postular</Button>
          </>
        )}
        {t.radio_status === "approved" && !t.featured && (
          <Button size="sm" data-testid={`track-feature-btn-${t.id}`} onClick={() => run(() => api("post", `/tracks/${t.id}/feature`), "¡Canción destacada por 7 días!")} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]"><Sparkles className="mr-1 h-3.5 w-3.5" />Destacar · {me?.is_pro ? 2 : 3} créditos</Button>
        )}
        {t.featured && <span className="font-mono text-[10px] text-slate-500">hasta {fmtDate(t.featured_until)}</span>}
        <button data-testid={`track-delete-btn-${t.id}`} onClick={() => window.confirm("¿Eliminar esta canción?") && run(() => api("delete", `/tracks/${t.id}`), "Canción eliminada")} className="p-2 text-slate-500 hover:text-red-400" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function TrackList({ tracks, reload }) {
  const channels = useChannels();
  if (!tracks.length) return <Empty>Todavía no subiste canciones.</Empty>;
  return <div data-testid="track-list">{tracks.map((t) => <TrackRow key={t.id} t={t} channels={channels} reload={reload} />)}</div>;
}

const PK_FIELDS = [["artist_name", "Nombre artístico"], ["location", "Ciudad, país"], ["instagram", "Instagram (URL)"], ["spotify", "Spotify (URL)"], ["youtube", "YouTube (URL)"], ["website", "Sitio web (URL)"], ["contact", "Contacto para prensa / booking"]];

export function PressKitEditor() {
  const api = useApi();
  const { me, refresh } = useMe();
  const [pk, setPk] = useState({ bio: "", ...Object.fromEntries(PK_FIELDS.map(([k]) => [k, ""])), ...(me?.presskit || {}) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api("put", "/me/presskit", pk); toast.success("Press Kit guardado"); refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api("post", "/me/photos", fd); toast.success("Foto subida"); refresh(); } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const removePhoto = async (url) => { await api("delete", "/me/photos", null, { params: { url } }); refresh(); };
  return (
    <div data-testid="presskit-editor" className="grid gap-8 lg:grid-cols-[3fr_2fr]">
      <div className="space-y-4 rounded-xl border border-white/10 bg-[#12121A] p-6">
        {PK_FIELDS.map(([k, label]) => <Input key={k} data-testid={`presskit-input-${k}`} placeholder={label} value={pk[k] || ""} onChange={(e) => setPk({ ...pk, [k]: e.target.value })} className={fieldCls} />)}
        <Textarea data-testid="presskit-input-bio" rows={6} placeholder="Biografía" value={pk.bio || ""} onChange={(e) => setPk({ ...pk, bio: e.target.value })} className={fieldCls} />
        <div className="flex items-center gap-4">
          <Button data-testid="presskit-save-btn" disabled={busy} onClick={save} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">{busy ? "Guardando..." : "Guardar Press Kit"}</Button>
          <Link data-testid="presskit-public-link" to={`/artista/${me?.user_id}`} className="flex items-center gap-1 text-sm text-[#FF7A45] hover:underline">Ver página pública <ExternalLink className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#12121A] p-6">
        <p className="font-heading text-lg font-bold text-white">Fotos de prensa</p>
        <p className="text-xs text-slate-500">La primera es la portada de tu Press Kit.</p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {me?.photos?.map((p) => (
            <div key={p} className="relative">
              <img src={mediaUrl(p)} alt="" className="aspect-square w-full rounded object-cover" />
              <button onClick={() => removePhoto(p)} className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white" aria-label="Quitar"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
        <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20">
          <Upload className="h-4 w-4" />Subir foto
          <input data-testid="presskit-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
        </label>
      </div>
    </div>
  );
}

export function SubmissionsTable({ subs }) {
  if (!subs.length) return <Empty>Aún no enviaste canciones a medios. <Link to="/medios" className="text-[#FF7A45] underline">Explora el directorio</Link>.</Empty>;
  return (
    <div data-testid="submissions-table" className="space-y-3">
      {subs.map((s) => (
        <div key={s.id} data-testid={`submission-${s.id}`} className="rounded-xl border border-white/10 bg-[#12121A] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-heading font-bold text-white">{s.track?.title} <span className="font-body font-normal text-slate-500">→ {s.media_name}</span></p>
              <p className="font-mono text-[11px] text-slate-500">{s.credits} créditos · enviado {fmtDate(s.created_at)} {s.status === "pending" && `· responde antes del ${fmtDate(s.deadline)}`}</p>
            </div>
            <StatusBadge status={s.status} testId={`submission-status-${s.id}`} />
          </div>
          {s.feedback && <p className="mt-3 border-l-2 border-[#FF4500] pl-3 text-sm text-slate-300">“{s.feedback}”</p>}
        </div>
      ))}
    </div>
  );
}

export function useArtistData() {
  const api = useApi();
  const [tracks, setTracks] = useState([]);
  const [subs, setSubs] = useState([]);
  const reload = () => {
    api("get", "/me/tracks").then(setTracks).catch(() => {});
    api("get", "/me/submissions").then(setSubs).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, []);
  return { tracks, subs, reload };
}
